import { gql, useQuery } from '@apollo/client'
import CloseIcon from '@mui/icons-material/Close'
import {
  Box,
  Grid,
  Card,
  Button,
  CardContent,
  IconButton,
  Typography,
  CardActions,
  CircularProgress,
} from '@mui/material'
import { Query, Share } from '@promoboxx/graphql-gateway-types'
import I18n from 'i18n-js'
import moment from 'moment'
import { useSelector } from 'react-redux'
import { makeStyles } from 'tss-react/mui'

import SocialPreview from '@src/components/SocialPreview'
import LinkChild from '@src/lib/LinkChild'
import { currentRetailerAccountAndBrandSlugSelector } from '@src/stores/app/selectors'

import reportingPaths from '../reporting/reportingPaths'

import { CalendarEventType } from './Calendar'

const useStyles = makeStyles()((theme) => ({
  loader: {
    display: 'flex',
    justifyContent: 'center',
    alignContent: 'center',
    padding: theme.spacing(12),
  },
  image: {
    width: '100%',
  },
  disablePaddingTop: {
    paddingTop: 0,
  },
  statsContainer: {
    paddingTop: theme.spacing(6),
  },
  previewContainer: {
    paddingTop: theme.spacing(6),
    backgroundColor: theme.palette.background.default,
  },
  actionsContainer: {
    position: 'absolute',
    right: 0,
  },

  card: {
    width: 'auto',
  },
  statsList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
    display: 'flex',
    justifyContent: 'center',
    flexWrap: 'wrap',
  },
  statsListItem: {
    display: 'flex',
    flexDirection: 'column',
    textAlign: 'center',
    margin: theme.spacing(0, 2),
  },
  statsNumber: {
    color: theme.palette.text.secondary,
    fontSize: theme.typography.h5.fontSize,
    marginBottom: theme.spacing(2),
  },
  statsLabel: {
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
  },
  shareLink: {
    marginTop: theme.spacing(6),
    color: theme.palette.primary.dark,
    textTransform: 'uppercase',
    textAlign: 'center',
    display: 'block',
    textDecoration: 'none',
    fontSize: theme.typography.caption.fontSize,
  },
  mobilePadding: {
    paddingTop: 28,
    [theme.breakpoints.up('sm')]: {
      paddingTop: 0,
    },
  },
}))

interface CampaignCardProps {
  event: CalendarEventType
  onClose: () => void
}

const ShareStats: React.FC<
  React.PropsWithChildren<{ share: Share; retailerAccount: string }>
> = ({ share, retailerAccount }) => {
  const { classes } = useStyles()
  let {
    pbxx_views = null,
    pbxx_actions = null,
    pbxx_consumer_clicks = null,
  } = share.internal_stats || {}
  let clicks =
    share.channel === 'facebook'
      ? share.link_clicks || 0
      : pbxx_consumer_clicks || 0
  return (
    <Grid container alignItems="center" spacing={5}>
      {share.channel !== 'instagram' ? (
        <Grid item xs={12}>
          <ul className={classes.statsList}>
            {pbxx_views !== null && pbxx_views >= 0 && (
              <li className={classes.statsListItem}>
                <span className={classes.statsNumber}>{pbxx_views}</span>
                <span className={classes.statsLabel}>
                  {I18n.t('shareCard.views')}
                </span>
              </li>
            )}
            {pbxx_actions !== null && pbxx_actions >= 0 && (
              <li className={classes.statsListItem}>
                <span className={classes.statsNumber}>{pbxx_actions}</span>
                <span className={classes.statsLabel}>
                  {I18n.t('shareCard.actions')}
                </span>
              </li>
            )}
            {
              <li className={classes.statsListItem}>
                <span className={classes.statsNumber}>{clicks}</span>
                <span className={classes.statsLabel}>
                  {I18n.t('shareCard.clicks')}
                </span>
              </li>
            }
          </ul>
        </Grid>
      ) : null}

      <Grid item xs={12}>
        <LinkChild
          to={reportingPaths.organicCampaignDetails.build({
            retailerAccountSlug: retailerAccount,
            campaignId: share.campaign?.link_id || '',
            channel: share.channel || '',
          })}
        >
          <Button fullWidth color="primary" variant="contained" size="large">
            {I18n.t('shareCard.viewReporting')}
          </Button>
        </LinkChild>
      </Grid>
      <Grid item xs={12}>
        <Typography align="center">
          {I18n.t('shareCard.sharedOn', {
            user:
              share.share_mode === 'auto_share'
                ? I18n.t('shareCard.autoShare')
                : `${share.created_by?.first_name} ${share.created_by?.last_name}`,
            shareDate: moment(share.executed_at).format('MM/DD/YY @ h:mm a'),
          })}
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <a
          className={classes.shareLink}
          href={`/#/${retailerAccount}/${share.campaign?.brand?.slug}/campaigns/${share.campaign?.link_id}/activate/${share.channel}?shareSettingId=${share.share_setting_id}`}
        >
          {I18n.t('shareCard.shareAgain')}
        </a>
      </Grid>
    </Grid>
  )
}

const FailedShare: React.FC<
  React.PropsWithChildren<{ share: Share; retailerAccount: string }>
> = ({ share, retailerAccount }) => {
  const { classes } = useStyles()
  return (
    <Grid container alignItems="center" spacing={5}>
      {share.status_message && (
        <Grid item xs={12}>
          <Typography align="center">{share.status_message}</Typography>
        </Grid>
      )}
      <Grid item xs={12}>
        <LinkChild
          to={reportingPaths.organicCampaignDetails.build({
            retailerAccountSlug: retailerAccount,
            campaignId: share.campaign?.link_id || '',
            channel: share.channel || '',
          })}
        >
          <Button fullWidth color="primary" variant="contained" size="large">
            {I18n.t('shareCard.viewReporting')}
          </Button>
        </LinkChild>
      </Grid>
      <Grid item xs={12}>
        <Typography align="center">
          {I18n.t('shareCard.sharedOn', {
            user:
              share.share_mode === 'auto_share'
                ? I18n.t('shareCard.autoShare')
                : `${share.created_by?.first_name} ${share.created_by?.last_name}`,
            shareDate: moment(share.executed_at).format('MM/DD/YY @ h:mm a'),
          })}
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <a
          className={classes.shareLink}
          href={`/#/${retailerAccount}/${share.campaign?.brand?.slug}/campaigns/${share.campaign?.link_id}/activate/${share.channel}?shareSettingId=${share.share_setting_id}`}
        >
          {I18n.t('shareCard.shareAgain')}
        </a>
      </Grid>
    </Grid>
  )
}

const EditShare: React.FC<
  React.PropsWithChildren<{ share: Share; retailerAccount: string }>
> = ({ share, retailerAccount }) => {
  return (
    <Grid container alignItems="center" spacing={5}>
      <Grid item xs={12}>
        <Typography align="center">{`${I18n.t(
          'shareCard.scheduledFor',
        )} ${moment(share.scheduled_for).format('L @h:mm:ss a')}`}</Typography>
      </Grid>
      <Grid item xs={12}>
        <Button
          fullWidth
          color="primary"
          variant="contained"
          size="large"
          href={`/#/${retailerAccount}/${share.campaign?.brand?.slug}/campaigns/${share.campaign?.link_id}/activate/${share.channel}?editShareId=${share.id}`}
        >
          {I18n.t('shareCard.editShare')}
        </Button>
      </Grid>
    </Grid>
  )
}
const CampaignCard: React.FC<React.PropsWithChildren<CampaignCardProps>> = ({
  event,
  onClose,
}) => {
  const { retailerAccount } = useSelector(
    currentRetailerAccountAndBrandSlugSelector,
  )
  const { classes } = useStyles()
  const { data, loading } = useQuery<Query>(GET_CALENDAR_SHARE, {
    variables: {
      params: {
        fields: [
          'campaign',
          'internal_stats',
          'facebook_page',
          'twitter_account',
          'share_setting',
          'share_setting__digital_assets',
          'created_by',
        ],
      },
      shareType: event.channel,
      shareId: event.item.associable_id,
    },
  })

  if (loading || !data?.getShare) {
    return (
      <Card raised={true} classes={{ root: classes.card }}>
        <div className={classes.loader}>
          <CircularProgress color="inherit" size={20} />
        </div>
      </Card>
    )
  }

  let ShareView: JSX.Element | null = null
  switch (data.getShare.status) {
    case 'success':
      ShareView = (
        <ShareStats share={data.getShare} retailerAccount={retailerAccount} />
      )
      break
    case 'scheduled':
      ShareView = (
        <EditShare share={data.getShare} retailerAccount={retailerAccount} />
      )
      break
    default:
      ShareView = (
        <FailedShare share={data.getShare} retailerAccount={retailerAccount} />
      )
      break
  }
  return (
    <Card raised={true} classes={{ root: classes.card }}>
      <CardActions disableSpacing classes={{ root: classes.actionsContainer }}>
        <IconButton onClick={onClose} size="large">
          <CloseIcon />
        </IconButton>
      </CardActions>
      <Box display="flex" flexWrap="nowrap" className={classes.shareLink}>
        <Grid container>
          <Grid item xs={12} sm={6}>
            <CardContent classes={{ root: classes.previewContainer }}>
              {data.getShare.share_setting && (
                <>
                  <SocialPreview share={data.getShare} />
                  <Typography variant="caption" gutterBottom>
                    {I18n.t('shareCard.preview')}
                  </Typography>
                  {(data.getShare.post_url || data.getShare.tweet_url) && (
                    <a
                      className={classes.shareLink}
                      href={
                        data.getShare.post_url || data.getShare.tweet_url || ''
                      }
                    >
                      {I18n.t('shareCard.viewShare', {
                        channel: data.getShare.channel,
                      })}
                    </a>
                  )}
                </>
              )}
            </CardContent>
          </Grid>
          <Grid item xs={12} sm={6}>
            <CardContent classes={{ root: classes.statsContainer }}>
              {ShareView}
            </CardContent>
          </Grid>
        </Grid>
      </Box>
    </Card>
  )
}

const GET_CALENDAR_SHARE = gql`
  query getShare(
    $params: ShareParams
    $shareType: ShareTypeParam!
    $shareId: ID!
  ) {
    getShare(params: $params, shareType: $shareType, shareId: $shareId) {
      id
      channel
      message
      subject
      link
      text_body
      html_body
      campaign {
        link_id
        id
        brand {
          id
          slug
        }
      }
      created_by {
        id
        first_name
        last_name
      }
      post_url
      created_at
      share_setting_id
      retailer_id
      status
      status_message
      internal_stats {
        pbxx_actions
        pbxx_views
        pbxx_consumer_clicks
      }
      updated_at
      updated_by_id
      link_clicks
      retailer_url_override
      scheduled_for
      share_mode
      executed_at
      description
      facebook_page {
        id
        name
        picture_url
      }
      tweet_url
      tweet_text
      twitter_account {
        id
        name
        nickname
        profile_image_url
      }
      share_setting {
        id
        name
        post_format
        responsive_share_image_url
        retailer_description
        retailer_link
        retailer_share_url_type
        retailer_title
        retailer_post_format
        digital_assets {
          id
          download_url
          external_video_url
          responsive_share_image_url
        }
        child_attachments {
          allow_retailer_url_override
          call_to_action
          description
          id
          link
          media {
            media_type
            source_type
            source_id
          }
          name
          share_url_type
          title
          url
        }
        share_data {
          share_data {
            post_format
            subject
            body
            title
            description
            link
            share_url_type
            button_text
          }
        }
      }
    }
  }
`
export default CampaignCard
